import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';
import { jvAgreementsApi, partnersApi, projectsApi } from '../../lib/api';
import { Users, DollarSign, Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

export const JVAgreementFormDialog = ({ open, onOpenChange, projectId, jvAgreement, onSuccess }) => {
  const isEditing = !!jvAgreement;
  const [project, setProject] = useState(null);
  const [flatSplit, setFlatSplit] = useState(false);

  const [activePartners, setActivePartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('manual');
  const [coInvestors, setCoInvestors] = useState([]);
  const [formData, setFormData] = useState({
    investor_name: '',
    investor_email: '',
    capital_contributed: '',
    partner_id: null,
    tier_1_cutoff_days: '180',
    tier_1_investor_split: '0.40',
    tier_2_cutoff_days: '270',
    tier_2_investor_split: '0.45',
    tier_3_cutoff_days: '365',
    tier_3_investor_split: '0.50',
    tier_4_cutoff_days: '456',
    tier_4_investor_split: '0.55',
    tier_5_investor_split: '0.60',
    // Granular expense responsibility splits (% investor responsible)
    purchase_split: '100',
    closing_split: '100',
    dd_split: '100',
    improvements_split: '100',
  });
  const [loading, setLoading] = useState(false);

  // Load active partners + the parent project (for capital-suggestion math).
  useEffect(() => {
    if (open) {
      partnersApi.list('active').then(res => {
        setActivePartners(res.data || []);
      }).catch(() => {});
      if (projectId) {
        projectsApi.getOne(projectId).then(res => {
          setProject(res.data || null);
        }).catch(() => setProject(null));
      }
    }
  }, [open, projectId]);

  // SOP audit: capital_contributed should equal purchase_price × purchase_split.
  // Compute the suggested value live so we can offer "Use Suggested" autofill.
  const purchaseSplitDecimal = (parseFloat(formData.purchase_split) || 0) / 100;
  const suggestedCapital = project && project.purchase_price
    ? Math.round(project.purchase_price * purchaseSplitDecimal * 100) / 100
    : null;
  const currentCapital = parseFloat(formData.capital_contributed) || 0;
  const showCapitalMismatch = (
    suggestedCapital !== null &&
    suggestedCapital > 0 &&
    Math.abs(currentCapital - suggestedCapital) > 0.5
  );

  useEffect(() => {
    if (jvAgreement) {
      setSelectedPartnerId(jvAgreement.partner_id || 'manual');
      setCoInvestors((jvAgreement.co_investors || []).map((c) => ({
        id: c.id,
        name: c.name || '',
        email: c.email || '',
        capital_committed: c.capital_committed != null ? String(c.capital_committed) : '',
      })));
      setFormData({
        investor_name: jvAgreement.investor_name || '',
        investor_email: jvAgreement.investor_email || '',
        capital_contributed: jvAgreement.capital_contributed?.toString() || '',
        partner_id: jvAgreement.partner_id || null,
        tier_1_cutoff_days: jvAgreement.tier_1_cutoff_days?.toString() || '180',
        tier_1_investor_split: jvAgreement.tier_1_investor_split?.toString() || '0.40',
        tier_2_cutoff_days: jvAgreement.tier_2_cutoff_days?.toString() || '270',
        tier_2_investor_split: jvAgreement.tier_2_investor_split?.toString() || '0.45',
        tier_3_cutoff_days: jvAgreement.tier_3_cutoff_days?.toString() || '365',
        tier_3_investor_split: jvAgreement.tier_3_investor_split?.toString() || '0.50',
        tier_4_cutoff_days: jvAgreement.tier_4_cutoff_days?.toString() || '456',
        tier_4_investor_split: jvAgreement.tier_4_investor_split?.toString() || '0.55',
        tier_5_investor_split: jvAgreement.tier_5_investor_split?.toString() || '0.60',
        purchase_split: ((jvAgreement.purchase_split ?? 1) * 100).toString(),
        closing_split: ((jvAgreement.closing_split ?? 1) * 100).toString(),
        dd_split: ((jvAgreement.dd_split ?? 1) * 100).toString(),
        improvements_split: ((jvAgreement.improvements_split ?? 1) * 100).toString(),
      });
      const _s1 = jvAgreement.tier_1_investor_split;
      setFlatSplit(_s1 != null && [2, 3, 4, 5].every(
        (n) => jvAgreement[`tier_${n}_investor_split`] === _s1
      ));
    } else {
      setSelectedPartnerId('manual');
      setCoInvestors([]);
      setFormData({
        investor_name: '',
        investor_email: '',
        capital_contributed: '',
        partner_id: null,
        tier_1_cutoff_days: '180',
        tier_1_investor_split: '0.40',
        tier_2_cutoff_days: '270',
        tier_2_investor_split: '0.45',
        tier_3_cutoff_days: '365',
        tier_3_investor_split: '0.50',
        tier_4_cutoff_days: '456',
        tier_4_investor_split: '0.55',
        tier_5_investor_split: '0.60',
        // SOP defaults: investor 100% of purchase/dd/improvements,
        // LF 100% of closing (so investor responsibility = 0).
        purchase_split: '100',
        closing_split: '0',
        dd_split: '100',
        improvements_split: '100',
      });
    }
  }, [jvAgreement, open]);

  const handlePartnerSelect = (partnerId) => {
    setSelectedPartnerId(partnerId);
    if (partnerId === 'manual') {
      setFormData(prev => ({ ...prev, investor_name: '', investor_email: '', partner_id: null }));
      return;
    }
    const p = activePartners.find(ap => ap.id === partnerId);
    if (p) {
      setFormData(prev => ({
        ...prev,
        investor_name: p.name,
        investor_email: p.email,
        partner_id: p.id,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        project_id: projectId,
        investor_name: formData.investor_name,
        investor_email: formData.investor_email ? formData.investor_email.toLowerCase() : undefined,
        capital_contributed: parseFloat(formData.capital_contributed),
        partner_id: formData.partner_id || undefined,
        tier_1_cutoff_days: parseInt(formData.tier_1_cutoff_days),
        tier_1_investor_split: parseFloat(formData.tier_1_investor_split),
        tier_2_cutoff_days: parseInt(formData.tier_2_cutoff_days),
        tier_2_investor_split: parseFloat(formData.tier_2_investor_split),
        tier_3_cutoff_days: parseInt(formData.tier_3_cutoff_days),
        tier_3_investor_split: parseFloat(formData.tier_3_investor_split),
        tier_4_cutoff_days: parseInt(formData.tier_4_cutoff_days),
        tier_4_investor_split: parseFloat(formData.tier_4_investor_split),
        tier_5_investor_split: parseFloat(formData.tier_5_investor_split),
        // Split form values arrive as 0–100 (UI percentage); persist as 0–1
        purchase_split: Math.max(0, Math.min(100, parseFloat(formData.purchase_split) || 0)) / 100,
        closing_split: Math.max(0, Math.min(100, parseFloat(formData.closing_split) || 0)) / 100,
        dd_split: Math.max(0, Math.min(100, parseFloat(formData.dd_split) || 0)) / 100,
        improvements_split: Math.max(0, Math.min(100, parseFloat(formData.improvements_split) || 0)) / 100,
        co_investors: coInvestors
          .filter((c) => (c.name || '').trim())
          .map((c) => ({
            id: c.id,
            name: c.name.trim(),
            email: c.email ? c.email.trim().toLowerCase() : null,
            capital_committed: parseFloat(c.capital_committed) || 0,
          })),
      };

      if (isEditing) {
        await jvAgreementsApi.update(jvAgreement.id, payload);
      } else {
        await jvAgreementsApi.create(payload);
      }

      onSuccess();
    } catch (error) {
      console.error('Failed to save JV agreement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEditing ? 'Edit JV Agreement' : 'Add JV Agreement'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Partner Selector */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Select Partner *
            </Label>
            <Select value={selectedPartnerId} onValueChange={handlePartnerSelect}>
              <SelectTrigger className="rounded-sm" data-testid="jv-partner-select">
                <SelectValue placeholder="Choose an active partner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual Entry</SelectItem>
                {activePartners.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <span>{p.name}</span>
                      <span className="text-xs text-muted-foreground">({p.entity_name || p.email})</span>
                      <span className="text-xs font-mono text-muted-foreground ml-auto">{fmt(p.total_available_capital)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activePartners.length === 0 && (
              <p className="text-xs text-amber-600">No active partners found. Promote a prospect from the Partners page first.</p>
            )}
          </div>

          {/* Name / Email (auto-filled from partner or manual) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Investor Name *</Label>
              <Input
                value={formData.investor_name}
                onChange={(e) => setFormData({ ...formData, investor_name: e.target.value })}
                required
                className="rounded-sm"
                placeholder="e.g., John Smith"
                readOnly={selectedPartnerId !== 'manual'}
                data-testid="jv-investor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Investor Email</Label>
              <Input
                type="email"
                value={formData.investor_email}
                onChange={(e) => setFormData({ ...formData, investor_email: e.target.value })}
                className="rounded-sm"
                placeholder="investor@example.com"
                readOnly={selectedPartnerId !== 'manual'}
                data-testid="jv-investor-email-input"
              />
            </div>
          </div>

          {selectedPartnerId !== 'manual' && (
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Linked to Partner</Badge>
              {(() => {
                const p = activePartners.find(ap => ap.id === selectedPartnerId);
                return p ? (
                  <span className="text-xs text-muted-foreground">
                    Available Capital: <span className="font-mono font-medium">{fmt(p.total_available_capital)}</span>
                  </span>
                ) : null;
              })()}
            </div>
          )}

          <div className="space-y-2">
            <Label>Capital Contributed ($) *</Label>
            <Input
              type="number"
              value={formData.capital_contributed}
              onChange={(e) => setFormData({ ...formData, capital_contributed: e.target.value })}
              required
              className="rounded-sm"
              placeholder="0"
              data-testid="jv-capital-input"
            />
            {project && project.purchase_price > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="jv-capital-suggestion-hint">
                Contractual baseline: <strong>{formatCurrency(suggestedCapital)}</strong>
                {' '}(= {formatCurrency(project.purchase_price)} × {formData.purchase_split}%).
                {' '}Real wires often differ slightly (HUD prorations, EMD already paid, seller credits) — that's fine.
                {showCapitalMismatch && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, capital_contributed: String(suggestedCapital) })}
                    className="ml-2 underline text-emerald-700 hover:text-emerald-900"
                    data-testid="jv-capital-use-suggested-btn"
                  >
                    Use baseline
                  </button>
                )}
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-2" data-testid="jv-co-investors-section">
            <div className="flex items-start justify-between gap-2">
              <div>
                <Label className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Investors on this JV (optional)</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  If several investors pool capital under this single agreement, list them here.
                  You can then tag each purchase, expense or wire to the investor who funded it.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm text-xs shrink-0"
                onClick={() => setCoInvestors([
                  ...coInvestors,
                  { id: (crypto?.randomUUID?.() || `ci-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`), name: '', email: '', capital_committed: '' },
                ])}
                data-testid="jv-add-co-investor-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Investor
              </Button>
            </div>
            {coInvestors.length > 0 && (
              <div className="space-y-2 pt-1">
                {coInvestors.map((ci, idx) => (
                  <div key={ci.id} className="grid grid-cols-12 gap-2 items-center p-2 bg-muted/30 rounded-sm" data-testid={`jv-co-investor-row-${idx}`}>
                    <div className="col-span-4">
                      <Input
                        value={ci.name}
                        placeholder="Investor name"
                        onChange={(e) => setCoInvestors(coInvestors.map((c, i) => i === idx ? { ...c, name: e.target.value } : c))}
                        className="rounded-sm text-sm"
                        data-testid={`jv-co-investor-name-${idx}`}
                      />
                    </div>
                    <div className="col-span-4">
                      <Input
                        value={ci.email}
                        type="email"
                        placeholder="email (optional)"
                        onChange={(e) => setCoInvestors(coInvestors.map((c, i) => i === idx ? { ...c, email: e.target.value } : c))}
                        className="rounded-sm text-sm"
                        data-testid={`jv-co-investor-email-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        value={ci.capital_committed}
                        type="number"
                        placeholder="Capital $"
                        onChange={(e) => setCoInvestors(coInvestors.map((c, i) => i === idx ? { ...c, capital_committed: e.target.value } : c))}
                        className="rounded-sm text-sm"
                        data-testid={`jv-co-investor-capital-${idx}`}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setCoInvestors(coInvestors.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Remove investor"
                        data-testid={`jv-remove-co-investor-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {(() => {
                  const total = coInvestors.reduce((s, c) => s + (parseFloat(c.capital_committed) || 0), 0);
                  const cap = parseFloat(formData.capital_contributed) || 0;
                  if (total > 0 && cap > 0 && Math.abs(total - cap) > 0.5) {
                    return (
                      <p className="text-[11px] text-amber-700" data-testid="jv-co-investor-mismatch">
                        Investor capital sums to {fmt(total)} but JV Capital Contributed is {fmt(cap)}. They don&apos;t have to match, but double-check if that&apos;s intentional.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>

          <Separator />

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Waterfall Tiers (5 Tiers)</p>

            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id="jv-flat-split"
                checked={flatSplit}
                onCheckedChange={(v) => {
                  const on = Boolean(v);
                  setFlatSplit(on);
                  if (on) {
                    const s = formData.tier_1_investor_split;
                    setFormData({
                      ...formData,
                      tier_2_investor_split: s,
                      tier_3_investor_split: s,
                      tier_4_investor_split: s,
                      tier_5_investor_split: s,
                    });
                  }
                }}
                data-testid="jv-flat-split-toggle"
              />
              <Label htmlFor="jv-flat-split" className="text-xs cursor-pointer">
                Flat profit split — one investor % for the whole deal (no hold-time tiers)
              </Label>
            </div>

            {flatSplit && (
              <div className="p-3 bg-muted/30 rounded-sm mb-3">
                <Label className="text-xs">Investor Split (decimal — applied to all 5 tiers)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.tier_1_investor_split}
                  onChange={(e) => setFormData({
                    ...formData,
                    tier_1_investor_split: e.target.value,
                    tier_2_investor_split: e.target.value,
                    tier_3_investor_split: e.target.value,
                    tier_4_investor_split: e.target.value,
                    tier_5_investor_split: e.target.value,
                  })}
                  className="rounded-sm text-sm"
                  data-testid="jv-flat-split-input"
                />
              </div>
            )}

            <div className={flatSplit ? 'hidden' : 'space-y-3'}>
              {[
                { num: 1, label: 'Tier 1', daysKey: 'tier_1_cutoff_days', splitKey: 'tier_1_investor_split', testId: '1' },
                { num: 2, label: 'Tier 2', daysKey: 'tier_2_cutoff_days', splitKey: 'tier_2_investor_split', testId: '2' },
                { num: 3, label: 'Tier 3', daysKey: 'tier_3_cutoff_days', splitKey: 'tier_3_investor_split', testId: '3' },
                { num: 4, label: 'Tier 4', daysKey: 'tier_4_cutoff_days', splitKey: 'tier_4_investor_split', testId: '4' },
              ].map(tier => (
                <div key={tier.num} className="p-3 bg-muted/30 rounded-sm">
                  <p className="text-xs font-medium mb-2">{tier.label}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Cutoff Days</Label>
                      <Input
                        type="number"
                        value={formData[tier.daysKey]}
                        onChange={(e) => setFormData({ ...formData, [tier.daysKey]: e.target.value })}
                        className="rounded-sm text-sm"
                        data-testid={`jv-tier${tier.testId}-days-input`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Investor Split (decimal)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData[tier.splitKey]}
                        onChange={(e) => setFormData({ ...formData, [tier.splitKey]: e.target.value })}
                        className="rounded-sm text-sm"
                        data-testid={`jv-tier${tier.testId}-split-input`}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="p-3 bg-muted/30 rounded-sm">
                <p className="text-xs font-medium mb-2">Tier 5 (final tier)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">No cutoff (final tier)</Label>
                  </div>
                  <div>
                    <Label className="text-xs">Investor Split (decimal)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.tier_5_investor_split}
                      onChange={(e) => setFormData({ ...formData, tier_5_investor_split: e.target.value })}
                      className="rounded-sm text-sm"
                      data-testid="jv-tier5-split-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Expense Responsibility Splits</p>
            <p className="text-xs text-muted-foreground mb-3">
              Investor's % responsibility per expense bucket. <strong>SOP defaults:</strong> Purchase 100%,
              DD 100%, Improvements 100%, Purchase Closing 0% (Manager's responsibility). When LF (Manager)
              pays an expense the investor is responsible for, it becomes a Manager Reimbursement and is
              repaid out of sale proceeds before the profit split.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'purchase_split', label: 'Purchase Price' },
                { key: 'closing_split', label: 'Closing Costs' },
                { key: 'dd_split', label: 'Due Diligence' },
                { key: 'improvements_split', label: 'Improvements' },
              ].map(b => (
                <div key={b.key} className="p-3 bg-muted/30 rounded-sm">
                  <Label className="text-xs">{b.label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData[b.key]}
                      onChange={(e) => setFormData({ ...formData, [b.key]: e.target.value })}
                      className="rounded-sm text-sm"
                      data-testid={`jv-${b.key.replace('_split', '')}-split-input`}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
              data-testid="submit-jv-btn"
            >
              {loading ? 'Saving...' : (isEditing ? 'Update JV Agreement' : 'Add JV Agreement')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
